import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, ShieldCheck, KeyRound, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { changePassword } from "../../api/auth";

export default function SecurityForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Password strength calculation
  const strength = useMemo(() => {
    if (!newPassword) return { score: 0, label: "", color: "" };
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (/[a-zA-Z]/.test(newPassword)) score++;
    if (/\d/.test(newPassword)) score++;
    if (/[^a-zA-Z0-9]/.test(newPassword)) score++;

    if (score <= 1) return { score: 25, label: "Weak", color: "#ef4444" };
    if (score === 2) return { score: 50, label: "Fair", color: "#f59e0b" };
    if (score === 3) return { score: 75, label: "Good", color: "#3b82f6" };
    return { score: 100, label: "Strong", color: "#22c55e" };
  }, [newPassword]);

  // Validation rules
  const hasMinLength = newPassword.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const isValid =
    currentPassword.length > 0 &&
    hasMinLength &&
    hasLetter &&
    hasNumber &&
    isMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;

    setSaving(true);
    setServerError(null);

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      toast.success("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      console.error("Failed to change password:", err);
      const msg = err.response?.data?.detail || "Failed to change password.";
      setServerError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* CHANGE PASSWORD CARD */}
      <form onSubmit={handleSubmit} className="profile-form-card" noValidate>
        <div>
          <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)", margin: 0 }}>
            Change Password
          </h3>
          <p style={{ fontSize: "0.78rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 2, margin: 0 }}>
            Ensure your account is using a long, unique password to stay secure
          </p>
        </div>

        {serverError && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 8,
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
              color: "#ef4444",
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertCircle size={16} />
            <span>{serverError}</span>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* CURRENT PASSWORD */}
          <div className="form-group">
            <label className="form-label" htmlFor="current_password">
              <span>Current Password</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="current_password"
                type={showCurrent ? "text" : "password"}
                className="form-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--dash-text-muted, #94a3b8)",
                  cursor: "pointer",
                }}
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* NEW PASSWORD */}
          <div className="form-group">
            <label className="form-label" htmlFor="new_password">
              <span>New Password</span>
              {strength.label && (
                <span style={{ color: strength.color, fontWeight: 700 }}>
                  {strength.label}
                </span>
              )}
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="new_password"
                type={showNew ? "text" : "password"}
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--dash-text-muted, #94a3b8)",
                  cursor: "pointer",
                }}
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {/* Strength Bar */}
            {newPassword && (
              <div className="pw-strength-bar-wrap">
                <div
                  className="pw-strength-bar"
                  style={{ width: `${strength.score}%`, background: strength.color }}
                />
              </div>
            )}
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="form-group">
            <label className="form-label" htmlFor="confirm_password">
              <span>Confirm New Password</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="confirm_password"
                type={showConfirm ? "text" : "password"}
                className={`form-input${confirmPassword && !isMatch ? " error" : ""}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--dash-text-muted, #94a3b8)",
                  cursor: "pointer",
                }}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmPassword && !isMatch && (
              <div className="form-error-text">Passwords do not match.</div>
            )}
          </div>

          {/* PASSWORD REQUIREMENTS LIST */}
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 8,
              background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.02))",
              border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.06))",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontSize: "0.78rem",
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)" }}>
              Password Requirements:
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: hasMinLength ? "#22c55e" : "var(--dash-text-muted, #94a3b8)" }}>
              <CheckCircle2 size={14} />
              <span>At least 8 characters long</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: hasLetter ? "#22c55e" : "var(--dash-text-muted, #94a3b8)" }}>
              <CheckCircle2 size={14} />
              <span>At least one letter (a-z, A-Z)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: hasNumber ? "#22c55e" : "var(--dash-text-muted, #94a3b8)" }}>
              <CheckCircle2 size={14} />
              <span>At least one number (0-9)</span>
            </div>
          </div>
        </div>

        {/* BUTTONS */}
        <div className="form-actions-row">
          <button
            type="submit"
            className="btn-primary"
            disabled={!isValid || saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Updating Password...</span>
              </>
            ) : (
              <span>Update Password</span>
            )}
          </button>
        </div>
      </form>

      {/* ADDITIONAL SECURITY ROWS (COMING SOON PILL) */}
      <div className="profile-form-card">
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)", margin: 0 }}>
          Advanced Security
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* 2FA ROW */}
          <div
            style={{
              padding: "16px",
              borderRadius: 8,
              background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.02))",
              border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.06))",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)" }}>
                Two-Factor Authentication (2FA)
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 2 }}>
                Add an extra layer of security using an authenticator app (TOTP)
              </div>
            </div>
            <span className="coming-soon-pill" title="We're working on this">COMING SOON</span>
          </div>

          {/* ACTIVE SESSIONS ROW */}
          <div
            style={{
              padding: "16px",
              borderRadius: 8,
              background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.02))",
              border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.06))",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)" }}>
                Active Login Sessions
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 2 }}>
                Manage and revoke active web & device sessions across your account
              </div>
            </div>
            <span className="coming-soon-pill" title="We're working on this">COMING SOON</span>
          </div>
        </div>
      </div>
    </div>
  );
}
